import { type IFixedRuleType } from '@framework/engine/schema/rules/IRuleType.ts';
import { getWidgetName } from '@framework/engine/validator/form-builder/getWidgetName.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { isWidgetIntegrable } from '../../../directives/integrable/IntegrableDirective.type.tsx';
import {
  hasMapCalculationLayers,
  MapCalculationConfigHashSuffix,
} from '../createMapSchema.ts';
import { computeIntersectionsCompetencesConfigHash } from '../drawing/intersection/utils/computeIntersectionsCompetencesConfigHash.ts';
import { type IMapWidget } from '../IMapWidget.ts';

/**
 * Regola fissa: se ci sono geometrie, l'hash salvato con le tabelle deve
 * coincidere con quello della configurazione attuale. Blocca l'invio quando
 * il ricalcolo automatico non è riuscito o non è partito, perché
 * intersezioni e competenze non corrisponderebbero alla configurazione.
 *
 * Passa comunque con l'hash assente, cioè sui dati salvati prima che
 * esistesse, e in integrazione sulle mappe non integrabili, che l'utente non
 * può ricalcolare.
 */
export const MapCalculationUpToDateRule: IFixedRuleType = {
  name: 'Intersezioni aggiornate',
  validator: (schema, widget) => {
    const mapWidget = widget as unknown as IMapWidget;
    if (!hasMapCalculationLayers(mapWidget)) return schema;

    const hashFieldName = `${getWidgetName(mapWidget)}${MapCalculationConfigHashSuffix}`;
    const expectedHash = computeIntersectionsCompetencesConfigHash(mapWidget);
    const isIntegrable = isWidgetIntegrable(mapWidget);

    return schema.test(
      'map-calculation-up-to-date',
      t(
        'rules.map-calculation-up-to-date.error',
        'Il calcolo delle intersezioni della mappa non è completo: attendere che termini oppure rimuovere e reinserire le geometrie.',
      ),
      function (geometries: unknown[] | null | undefined) {
        if (!geometries?.length) return true;
        const isIntegration = this.options.context?.isIntegration === true;
        if (isIntegration && !isIntegrable) return true;
        const savedHash = this.parent?.[hashFieldName];
        return savedHash == null || savedHash === expectedHash;
      },
    );
  },
};
